import React, { useState } from "react";
import CustomSelect from "../../globalcontrols/customselect/customselect";
import "./css/inventoryInsights.css";
import { analyticsUnitLabel, stockUnitLabel } from "../../pos/logic/saleUnits";

const statusLabels = {
  slow: "Lento",
  stale: "Estancado",
  critical_stale: "Crítico",
  never_sold: "Sin ventas",
  out_of_stock: "Sin stock",
};

const formatDateTime = (value) => value
  ? new Intl.DateTimeFormat("es-CL", { dateStyle: "short", timeStyle: "short" }).format(new Date(value))
  : "Nunca";

function InventoryInsights({
  data,
  loading,
  error,
  locations,
  selectedLocation,
  setSelectedLocation,
}) {
  const [activeView, setActiveView] = useState("attention");
  const views = [
    { id: "attention", label: "Requieren atención" },
    { id: "recent_sales", label: "Vendidos recientemente" },
    { id: "fast_movers", label: "Más vendidos" },
  ];
  const items = data?.[activeView] || [];

  return (
    <section className="dashboard-card inventory-card" aria-labelledby="inventory-title">
      <div className="card-heading inventory-heading">
        <div>
          <p className="card-kicker">Inventario</p>
          <h2 id="inventory-title">Salud del stock</h2>
        </div>
        {data?.generated_at && <span className="updated-at">Actualizado {formatDateTime(data.generated_at)}</span>}
      </div>

      <div className="inventory-location">
        <CustomSelect
          label="Ubicación"
          options={locations}
          selectedValue={selectedLocation}
          onChange={setSelectedLocation}
          labelExtractor={(item) => item.name}
          valueExtractor={(item) => item.id.toString()}
        />
      </div>

      {data?.counts && (
        <div className="inventory-counts" aria-label="Estado del inventario">
          <button type="button" onClick={() => setActiveView("attention")}>
            <strong>{data.counts.slow + data.counts.stale + data.counts.critical_stale + data.counts.never_sold}</strong>
            <span>Atención</span>
          </button>
          <div><strong>{data.counts.out_of_stock}</strong><span>Sin stock</span></div>
          <div><strong>{data.counts.healthy}</strong><span>Saludables</span></div>
        </div>
      )}

      <div className="segmented-control" role="tablist" aria-label="Vistas de inventario">
        {views.map((view) => (
          <button
            key={view.id}
            type="button"
            role="tab"
            aria-selected={activeView === view.id}
            onClick={() => setActiveView(view.id)}
          >
            {view.label}
          </button>
        ))}
      </div>

      {error && <div className="dashboard-alert" role="alert">{error}</div>}
      {loading ? (
        <div className="dashboard-loading" role="status">Analizando inventario…</div>
      ) : items.length === 0 ? (
        <div className="empty-state">No hay productos en esta categoría.</div>
      ) : (
        <ul className="inventory-list">
          {items.map((item) => (
            <li key={`${item.id || item.code}-${item.location}-${item.created_at || item.status}`}>
              <div className="inventory-product">
                <strong>{item.description || item.code}</strong>
                <span>{item.location_name || "Sin ubicación"} · {item.code}</span>
              </div>
              {activeView === "recent_sales" ? (
                <div className="inventory-detail">
                  <strong>{Number(item.quantity || 0).toLocaleString("es-CL")} {stockUnitLabel(item.sale_unit)} vendidos</strong>
                  <span>Stock {Number(item.remaining_qty || 0).toLocaleString("es-CL")} {stockUnitLabel(item.sale_unit)}</span>
                  <span>{formatDateTime(item.created_at)}</span>
                </div>
              ) : activeView === "fast_movers" ? (
                <div className="inventory-detail">
                  <strong>{Number(item.sold_30_days || 0).toLocaleString("es-CL")} {analyticsUnitLabel(item.sale_unit)}</strong>
                  <span>Últimos 30 días</span>
                </div>
              ) : (
                <div className="inventory-detail">
                  <span className={`status-badge status-${item.status}`}>{statusLabels[item.status]}</span>
                  <span>Stock {Number(item.qty || 0).toLocaleString("es-CL")} {stockUnitLabel(item.sale_unit)} · {item.inactive_days === null ? "nunca vendido" : `${item.inactive_days} días`}</span>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {data?.stock_mismatches?.length > 0 && (
        <div className="mismatch-notice" role="status">
          <span className="material-symbols-outlined" aria-hidden="true">warning</span>
          {data.stock_mismatches.length} productos requieren revisión de stock.
        </div>
      )}
    </section>
  );
}

export default InventoryInsights;
